import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../lib/api";
import { ModelSelector, type ModelSelectorValue } from "./ModelSelector";
import { ModelFallbackSection, fallbackToForm, formToFallback } from "./ModelFallbackSection";
import { useUnsavedGuard, blockedSave } from "./unsavedGuard";
import { SavedNote } from "../shared/Icons";

export function ConfigModelo({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const [provider, setProvider] = useState(agent.model_provider ?? "openai");
  const [modelName, setModelName] = useState(agent.model_name ?? "");
  const [temperature, setTemperature] = useState<number>(agent.temperature ?? 0.7);
  const [reasoningEffort, setReasoningEffort] = useState<string>(agent.reasoning_effort ?? "off");
  const [verbosity, setVerbosity] = useState<string>(agent.verbosity ?? "default");
  const [maxTokens, setMaxTokens] = useState<number>(agent.max_tokens ?? 4096);
  const [fallback, setFallback] = useState<ModelSelectorValue | null>(
    fallbackToForm(agent.model_fallback),
  );

  const current = JSON.stringify({ provider, modelName, temperature, reasoningEffort, verbosity, maxTokens, fallback });
  const [savedSnap, setSavedSnap] = useState(current);

  const mut = useMutation({
    mutationFn: () =>
      api.patch(`/agents/${agent.id}`, {
        model_provider: provider,
        model_name: modelName,
        temperature,
        reasoning_effort: reasoningEffort === "off" ? null : reasoningEffort,
        verbosity: verbosity === "default" ? null : verbosity,
        max_tokens: maxTokens,
        model_fallback: formToFallback(fallback),
      }),
    onSuccess: () => {
      setSavedSnap(JSON.stringify({ provider, modelName, temperature, reasoningEffort, verbosity, maxTokens, fallback }));
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
    },
  });

  useUnsavedGuard(current !== savedSnap, modelName ? mut.mutateAsync : blockedSave);

  return (
    <div style={{ maxWidth: 480, display: "flex", flexDirection: "column", gap: 16 }}>
      <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", margin: 0 }}>
        Modelo principal
      </h4>
      <ModelSelector
        showMaxTokens
        showVerbosity
        value={{ provider, model_name: modelName, temperature, reasoning_effort: reasoningEffort, verbosity, maxTokens }}
        onChange={(next) => {
          setProvider(next.provider);
          setModelName(next.model_name);
          setTemperature(next.temperature);
          setReasoningEffort(next.reasoning_effort);
          setVerbosity(next.verbosity ?? "default");
          setMaxTokens(next.maxTokens ?? 4096);
        }}
      />

      <ModelFallbackSection showVerbosity value={fallback} onChange={setFallback} />

      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending || !modelName}
        style={{
          background: "var(--brand)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "8px 20px",
          fontSize: 13,
          cursor: modelName ? "pointer" : "not-allowed",
          alignSelf: "flex-start",
          opacity: modelName ? 1 : 0.5,
        }}
      >
        {mut.isPending ? "Salvando..." : "Salvar"}
      </button>
      <SavedNote show={mut.isSuccess} />
    </div>
  );
}
